import { Button, Modal, Text } from '@ksyos/design-system';
import type { ISessionUser } from '@frontend/configureStore';
import { getSelectedLanguage } from '@frontend/translate';
import { useAutoSwitchOrganization } from '@frontend/utils/hooks/useAutoSwitchOrganization';

interface AutoSwitchOrganizationProps {
    children: React.ReactNode;
    forwardTo: (updatedSession: ISessionUser) => string;
    nextOrganizationId?: string;
    showWarningModal?: boolean;
}

/**
 * This component automatically switches the organization employment based on the provided parameters.
 * If the `nextOrganizationId` param is omitted, it will check the URL for the "`switchToOrganizationId=...`" query parameter.
 * This is done, so we can switch the employment automatically when the user clicks a link from an e-mail while still having another employment selected.
 * @param {React.ReactNode} props.children - The children of the component.
 * @param {string} props.forwardTo - The URL to forward to after switching employment. This is a function that also provides the updated session.
 * @param {string} [props.nextOrganizationId] - Optionally you can give it the organization of the next employment to switch to.
 * @param {boolean} [props.showWarningModal] - Optionally you can give it a boolean to show a warning modal before switching employment.
 */
export function AutoSwitchOrganization(props: Readonly<AutoSwitchOrganizationProps>) {
    const translate = getSelectedLanguage();

    const { autoSwitchToOrganization, shouldSwitchToOrganization } = useAutoSwitchOrganization({
        forwardTo: props.forwardTo,
        nextOrganizationId: props.nextOrganizationId,
        showWarningModal: props.showWarningModal,
    });

    return (
        <>
            {!shouldSwitchToOrganization && props.children}

            <Modal
                isOpen={(props.showWarningModal && shouldSwitchToOrganization) ?? false}
                onClose={() => {}}
                size="medium"
            >
                <Modal.Header>{translate.autoSwitchOrganization.warningModalTitle}</Modal.Header>
                <Modal.Body>
                    <Text>{translate.autoSwitchOrganization.warningModalBody}</Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={autoSwitchToOrganization}>{translate.common.continue}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
